import React, {forwardRef, useLayoutEffect, useState} from 'react'

import ProjectsListManager from "../../../../core/gd/manager/projects-list-manager"
import NavigationMenuItemProject from "./menu-item-project"
import ArrayUtils from "../../../../core/gd/utils/array"
import NavigationMenu_ProjectsInlineAdd from "../../sections/projects/tree/inline-add"
import ProjectSession from "../../../../core/model2/project/project-session"
import {BUS_APP_LAUNCH, PROJECT_ITEM_VIEW_CLOSE} from "../../../../core/gd/app-manager/@event"
import {NavigationMenuInlineAddParams, NavigationMenuInlineAddType} from "../menu-add-with-popover/@types"
import {useForceUpdate} from "../../../../core/gd/utils/dom-and-react/react-utils"
import menu_getSortedChildProjects from "./functions/order-projects"

type Props = {
    localSettings                   : any
    expandedProjectsSettingName?    : string

    projectId                       : ID

    colorized?                      : boolean
}

// const debug = "NavigationMenuItemProjectWithChildren"
const debug = false

const NavigationMenuItemProjectWithChildren = forwardRef((props: Props, ref: any) => {

    const expandedProjectsSettingName = props.expandedProjectsSettingName || 'expandedProjects'

    const [inlineAddParams, setInlineAddParams] = useState<any>(null)   // parameters for inline add
    const [processingAdd, setProcessingAdd] = useState<any>(false)      // is true when inline add processing is in progress and changes are awaited

    const project = gd.session.projects.get(props.projectId)!

    const forceUpdate = useForceUpdate()

    useLayoutEffect(() => {
        const unsubscribeLaunch = gd.bus.subscribeNoContext(BUS_APP_LAUNCH, onLaunchApp)
        const unsubscribeItemViewClose = gd.bus.subscribeNoContext(PROJECT_ITEM_VIEW_CLOSE, onItemViewClose) // for apps like project report view

        return () => {
            unsubscribeLaunch()
            unsubscribeItemViewClose()
        }
    }, [])

    useLayoutEffect(() => {
        // if we are waiting for project to be added and treeChildren is modified then hide inline add before rerender
        if (processingAdd) {
            setInlineAddParams(null)
            setProcessingAdd(false)
        }
    }, [project.treeChildren.length])

    const onLaunchApp = (data: any) => {
        if (!data.params.projectId) return

        const projectParentsIds = gd.tree.findProjectParents(gd.tree.getProject(data.params.projectId)).map((p:any)=>p.id)
        if (projectParentsIds.includes(props.projectId)) {
            props.localSettings[expandedProjectsSettingName] = _.union(props.localSettings[expandedProjectsSettingName].slice(), projectParentsIds)
            props.localSettings.save()
        }

        forceUpdate()
    }

    const onItemViewClose = (project: ProjectSession) => {
        forceUpdate()
    }

    const onClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const project = gd.session.projects.get(props.projectId)!
        const children = ProjectsListManager.getChildProjects(null, props.projectId);

        if (children && project.properties?.handleExpandInsteadOfOpen) {
            onMenuItemHandleClick(props.projectId, e)
        } else {
            gd.goTo("p/"+props.projectId)
            gd.navigation.goToProject(props.projectId, '')
        }
    }

    const onMenuItemHandleClick = (id: ID, e: React.MouseEvent) => {
        e.stopPropagation()

        const newExpandedItems = props.localSettings[expandedProjectsSettingName].slice()
        const index = newExpandedItems.indexOf(id)
        if (index !== -1) newExpandedItems.splice(index, 1)
        else newExpandedItems.push(id)

        props.localSettings[expandedProjectsSettingName] = newExpandedItems
        props.localSettings.save()

        forceUpdate()
    }

    const onAddInline = (inlineType: NavigationMenuInlineAddType, parentId: ID, params: NavigationMenuInlineAddParams) => {
        debug && console.log(debug, 'onAddInline', inlineType, parentId, params)

        const expandedProjectIds = props.localSettings[expandedProjectsSettingName]
        props.localSettings[expandedProjectsSettingName] = ArrayUtils.addValueToArray(expandedProjectIds, parentId)
        props.localSettings.save()

        setInlineAddParams({
            inlineType  : inlineType,
            addParentId : parentId,
            params      : params, // for folder, page and embed
        })
    }

    const onInlineAdded = (isAdded: boolean) => {
        debug && console.log(debug, 'onInlineAdded', isAdded)

        // if nothing added and project has no children then collapse it
        if (!isAdded && _.isEmpty(ProjectsListManager.getChildProjects(null, inlineAddParams?.addParentId))) {
            const expandedProjectIds = props.localSettings[expandedProjectsSettingName]
            props.localSettings[expandedProjectsSettingName] = ArrayUtils.removeValueFromArray(expandedProjectIds, inlineAddParams.addParentId)
            props.localSettings.save()
        }

        if (isAdded) {
            setProcessingAdd(true)
        } else {
            setInlineAddParams(null)
        }
    }

    const renderListItems = (level: number, parent: ProjectSession | null, submenu: any) => {
        let items:ProjectSession[] = []

        if (!parent) {
            const sessionProject = gd.session.projects.get(props.projectId)
            if (sessionProject) {
                items.push(sessionProject)
            } else {
                console.error("#9d82389728372dd - no project for pin/starred found at all !")
            }
        } else {
            items = menu_getSortedChildProjects(parent) // parent.treeChildren
        }

        if (items && items.length > 0) {
            _.map(items, (listItem)=>{
                const isExpanded        = props.localSettings[expandedProjectsSettingName]?.includes(listItem.id)
                const showChildProjects = isExpanded && !_.isEmpty(listItem.treeChildren)
                const showInlineAdd     = inlineAddParams?.addParentId === listItem.id
                const fragment          = gd.router.history.fragment || ''
                const isSelected        = _.startsWith(fragment, 'p/') && fragment.includes(listItem.id)

                const sessionProject    = gd.session.projects.get(listItem.id)

                // this hides archived pins
                if (!sessionProject || sessionProject.isArchived) {
                    return
                }

                submenu.push(
                    <NavigationMenuItemProject
                        key                     = {listItem.id}
                        colorized               = {props.colorized}
                        project                 = {listItem}
                        isSelected              = {isSelected}
                        isExpanded              = {isExpanded}
                        level                   = {level}
                        onMenuItemHandleClick   = {onMenuItemHandleClick}
                        onAddInline             = {onAddInline}
                        {...props}
                    />)


                if (showChildProjects) {
                    renderListItems(level+1, listItem, submenu);
                }

                if (showInlineAdd) {
                    submenu.push(
                        <NavigationMenu_ProjectsInlineAdd
                            key         = {'add_{0}'.f(listItem.id)}
                            type        = {inlineAddParams.inlineType}
                            level       = {level+1}
                            parentId    = {inlineAddParams.addParentId}
                            params      = {inlineAddParams.params}
                            callback    = {onInlineAdded}
                        />)
                }
            })
        }
    }

    const submenu: any[] = []
    renderListItems(0, null, submenu)

    return (
        <div ref={ref} className="root-pinned-project-container with-children">
            {submenu}
        </div>
    )
})

export default NavigationMenuItemProjectWithChildren